<template>
  <div class="header-wrapper">
    <div class="fixed-header-container">
      <!-- 1. 顶部条：保留文字内容，调整功能布局 -->
      <div class="top-bar">
        <div class="container flex justify-between items-center h-full">
          <!-- 左侧：保持显示内容不变 -->
          <div class="system-info">
            普洛汇.商城系统 PRO - 赋能未来实验室 &nbsp; 订购/客服：<span style="color: #00B4D8;">15618138852</span>
          </div>

          <!-- 右侧：登录状态、订单查询、帮助中心、退出账号、购物车 -->
          <div class="top-actions flex items-center gap-1">
                        <template v-if="!userInfo">
              <div class="top-auth-group">
                <span class="auth-link login-action" @click="handleLogin"><i class="fas fa-sign-in-alt"></i> 登录</span>
                <span class="auth-link register-action" @click="handleRegister"><i class="fas fa-user-plus"></i> 免费注册</span>
              </div>
            </template>
            <template v-else>
              <span class="user-welcome-link" @click="goPersonal">
                <span class="user-avatar-mini">{{ (userInfo.customerName || userInfo.name || '用').charAt(0) }}</span>
                <span class="user-welcome-text">欢迎您，{{ userInfo.customerName || userInfo.name }}</span>
              </span>
              <span class="auth-link logout-btn" @click="handleLogout">
                退出账号 
              </span>
            </template>

            <!-- 恢复订单查询与帮助中心 -->
            <el-divider direction="vertical" class="border-gray-600/50" />
            
            <span class="auth-link" @click="goOrder">
              <i class="fas fa-file-invoice mr-1"></i> 订单查询
            </span>
            <span class="auth-link" @click="router.push('/help')">
              <i class="fas fa-headset mr-1"></i> 帮助中心
            </span>

            <!-- 购物车：移至顶层最右侧，使用图片中的绿色角标 -->
            <el-divider direction="vertical" class="border-gray-600/50" />
            <el-badge 
              :value="cartCount" 
              class="top-cart-badge" 
              :hidden="cartCount === 0"
              :offset="[12, 17]"
            >
              <span class="auth-link" @click="goCart">
                <i class="fas fa-shopping-cart mr-1"></i> 购物车
              </span>
            </el-badge>
          </div>
        </div>
      </div>

      <!-- 2. 主导航及 Hero 区域 -->
      <header class="main-header flex flex-col relative" :class="{ 'is-collapsed-header': isCollapsed }">
        <div class="container flex-1">
          <!-- 导航行 -->
          <div class="nav-row flex items-center justify-between h-20 relative z-10">
            <!-- Logo 区域 -->
            <a href="#/" class="logo-box flex items-center" @click.prevent="goHome">
              <img src="@/assets/images/login/logo_plh.png" alt="普洛汇" class="header-logo-img" />
            </a>

            <!-- 中间导航菜单 -->
            <nav class="nav-links-center">
              <div 
                v-for="(item, index) in navItems" 
                :key="index" 
                class="nav-item-text"
                @click="goToRoute(item)"
              >
                {{ item }}
              </div>
            </nav>

            <!-- 右侧：迷你搜索框 -->
            <div class="header-right-tools flex items-center">
              <div class="mini-search-bar" :class="{ 'is-active': isCollapsed }">
                <el-input 
                  v-model.trim="searchQuery" 
                  :placeholder="placeholderText"
                  @input="searchQuery = String($event || '').trim()"
                  @keydown.enter="handleSearch($event)"
                  class="dark-mini-input"
                  clearable
                >
                  <template #prefix>
                    <el-icon class="text-gray-400 text-base"><Search /></el-icon>
                  </template>
                  <template #suffix>
                    <div
                      class="text-[13px] text-white px-4 h-[30px] flex items-center justify-center cursor-pointer transition-all font-bold tracking-wide hover:opacity-90"
                      style="background: #00B4D8; border-radius: 6px; margin-left: 6px; margin-right: -2px; order: 2;"
                      @click="handleSearch"
                    >
                      搜索 <i class="fas fa-arrow-right ml-1 text-[12px]"></i>
                    </div>
                  </template>
                </el-input>
              </div>

              <button type="button" class="ai-pill-btn shrink-0" @click="handleOpenAI">
                <i class="far fa-comment-dots ai-icon-gradient"></i>
                <span class="font-bold ml-2">AI助手</span>
              </button>
            </div>

            <div class="mobile-header-actions">
              <template v-if="!userInfo">
                <button class="mobile-auth-btn" type="button" @click="handleLogin">登录</button>
                <button class="mobile-auth-btn is-primary" type="button" @click="handleRegister">注册</button>
              </template>
              <template v-else>
                <button class="mobile-icon-btn" type="button" aria-label="个人中心" @click="goPersonal">
                  <i class="fas fa-user"></i>
                </button>
                <el-badge :value="cartCount" :hidden="cartCount === 0" :offset="[2, 5]" class="mobile-cart-badge">
                  <button class="mobile-icon-btn" type="button" aria-label="购物车" @click="goCart">
                    <i class="fas fa-shopping-cart"></i>
                  </button>
                </el-badge>
              </template>
              <button class="mobile-icon-btn" type="button" aria-label="搜索" @click="handleMobileSearch">
                <i class="fas fa-search"></i>
              </button>
              <button class="mobile-icon-btn mobile-ai-btn" type="button" aria-label="AI助手" @click="handleOpenAI">
                <i class="far fa-comment-dots"></i>
              </button>
              <button class="mobile-icon-btn" type="button" aria-label="菜单" @click="mobileMenuVisible = true">
                <i class="fas fa-bars"></i>
              </button>
            </div>
          </div>

          <!-- 3. 图片文字及搜索栏复现 (采用 Grid 动画实现丝滑折叠) -->
          <div class="hero-transition-wrapper relative z-0" :class="{ 'is-collapsed': isCollapsed }">
            <div class="hero-content-inner overflow-hidden">
              <div class="pt-12 pb-14 flex flex-col items-center text-center">
                 <div class="badge-tag mb-6">
                    <i class="fas fa-bolt text-cyan-400 mr-2"></i>
                    <span>普洛汇 正式上线，探索未来实验室</span>
                 </div>
                 
                 <!-- 主标题 -->
                 <h1 class="hero-title mb-6">
                    精准试剂，<span class="gradient-text">赋能每一次伟大发现</span>
                 </h1>

                 <!-- 副标题 -->
                 <p class="hero-subtitle mb-10 max-w-2xl">
                    提供海量高品质化学试剂、生命科学耗材<br>通过货号、CAS号或产品名称，立刻开启您的极速采购体验
                 </p>

                 <!-- 主搜索栏复现 -->
                 <div class="hero-search-wrapper">
                    <div class="hero-search-box">
                       <el-icon class="search-prefix-icon"><Search /></el-icon>
                       <input 
                          v-model.trim="searchQuery" 
                          type="text" 
                          :placeholder="placeholderText"
                          @input="$event.target.value = $event.target.value.trim(); searchQuery = $event.target.value"
                          @keydown.enter="handleSearch($event)"
                       />
                       <el-icon v-if="searchQuery" class="search-clear-icon" @click="searchQuery = ''"><Close /></el-icon>
                       <button class="hero-search-btn" @click="handleSearch">
                          搜索 <i class="fas fa-arrow-right ml-1"></i>
                       </button>
                    </div>
                 </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 4. 底部展开/收缩控制台 (居中横条风格) -->
        <div class="w-full flex justify-center pb-2.5 relative z-10 shrink-0 mt-2">
          <div 
            class="flex items-center justify-center gap-1.5 text-white/50 hover:text-white/90 cursor-pointer transition-all text-[12px] px-5 py-1.5 rounded-full hover:bg-white/10 backdrop-blur-sm"
            @click="toggleBanner"
            :title="isCollapsed ? '展开横幅' : '收起横幅'"
          >
            <el-icon>
              <ArrowDown v-if="isCollapsed" />
              <ArrowUp v-else />
            </el-icon>
          </div>
        </div>
      </header>
    </div>

    <!-- 5. 客户自助 AI 助手 -->
    <AiOrderDrawer ref="aiOrderDrawerRef" @login-required="handleAssistantLoginRequired" />
    <AuthDrawer ref="authDrawerRef" />

    <div class="floating-action-stack" aria-label="快捷操作">
      <button
        type="button"
        class="floating-cart-entry"
        :class="{ 'is-pulsing': cartPulse }"
        :aria-label="'购物车，共 ' + cartCount + ' 件商品'"
        title="购物车"
        @click="goFloatingCart"
      >
        <span v-if="cartCount > 0" class="floating-cart-badge" aria-live="polite">{{ cartBadgeText }}</span>
        <i class="fas fa-shopping-cart floating-cart-icon" aria-hidden="true"></i>
        <span>购物车</span>
      </button>

      <button type="button" class="ai-floating-entry" aria-label="打开客户自助助手" @click="handleOpenAI">
        <img
          class="ai-floating-logo"
          src="@/assets/images/ai-assistant-z-logo.png"
          alt=""
          aria-hidden="true"
        />
        <span>自助助手</span>
      </button>
    </div>

    <el-drawer
      v-model="mobileMenuVisible"
      direction="rtl"
      size="min(88vw, 340px)"
      :with-header="false"
      append-to-body
      class="mobile-menu-drawer"
    >
      <div class="mobile-menu-panel">
        <div class="mobile-menu-head">
          <img src="@/assets/images/login/logo_plh.png" alt="普洛汇" />
          <button type="button" aria-label="关闭菜单" @click="mobileMenuVisible = false">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="mobile-menu-search" @click="handleMobileSearch">
          <i class="fas fa-search"></i>
          <span>货号、CAS号或产品名称</span>
        </div>

        <div class="mobile-menu-section">
          <button
            v-for="item in mobileMainLinks"
            :key="item.path"
            type="button"
            class="mobile-menu-item"
            @click="navigateTo(item.path)"
          >
            <span><i :class="item.icon"></i>{{ item.label }}</span>
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>

        <div class="mobile-menu-section">
          <button type="button" class="mobile-menu-item mobile-menu-ai" @click="handleOpenAI">
            <span><i class="far fa-comment-dots"></i>AI助手</span>
            <i class="fas fa-chevron-right"></i>
          </button>
          <template v-if="!userInfo">
            <button type="button" class="mobile-menu-cta" @click="handleLogin">登录</button>
            <button type="button" class="mobile-menu-cta is-outline" @click="handleRegister">免费注册</button>
          </template>
          <template v-else>
            <button type="button" class="mobile-menu-item" @click="goPersonal">
              <span><i class="fas fa-user-circle"></i>个人中心</span>
              <i class="fas fa-chevron-right"></i>
            </button>
            <button type="button" class="mobile-menu-item" @click="goOrder">
              <span><i class="fas fa-file-invoice"></i>订单查询</span>
              <i class="fas fa-chevron-right"></i>
            </button>
            <button type="button" class="mobile-menu-item" @click="goCart">
              <span><i class="fas fa-shopping-cart"></i>购物车</span>
              <i class="fas fa-chevron-right"></i>
            </button>
            <button type="button" class="mobile-menu-cta is-outline is-danger" @click="handleLogout">退出账号</button>
          </template>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted, computed, nextTick } from 'vue';
import { useRouter, useRoute } from "vue-router";
import { useUserStore } from "@/stores/user";
import { useCartStore } from "@/stores/cart";
import { storeToRefs } from "pinia";
import { ElMessage, ElMessageBox } from "element-plus";
import { Search, Close, ArrowUp, ArrowDown } from '@element-plus/icons-vue'
import AiOrderDrawer from './AiOrderDrawer.vue';
import AuthDrawer from '@/pc/views/Login/AuthDrawer.vue';
import { refreshStoredUserInfo } from '@/utils/userInfoRefresh';

const userStore = useUserStore();
const cartStore = useCartStore();
const router = useRouter();
const route = useRoute(); // 新增引入 route

const { userInfo } = storeToRefs(userStore);
const cartCount = computed(() => cartStore.totalNum);
const cartBadgeText = computed(() => Number(cartCount.value || 0) > 99 ? '99+' : Number(cartCount.value || 0));
const cartPulse = ref(false);
let cartPulseTimer = null;
// 纯手动控制的 isCollapsed
const isCollapsed = ref(false);
const src = ref(require('../../../../assets/images/login/logo_nav.png'));
const aiOrderDrawerRef = ref(null);
const authDrawerRef = ref(null);
const mobileMenuVisible = ref(false);

const searchQuery = ref("");
const placeholderText = ref('货号、CAS号或产品名称');
const navItems = ['首页', '品牌专区', '走进普洛汇', '工具'];
const mobileMainLinks = [
  { label: '首页', path: '/', icon: 'fas fa-home' },
  { label: '完整目录', path: '/catalogue', icon: 'fas fa-th-large' },
  { label: '品牌专区', path: '/brands', icon: 'fas fa-award' },
  { label: '走进普洛汇', path: '/puluohui', icon: 'fas fa-building' },
  { label: '工具', path: '/tools', icon: 'fas fa-flask' },
  { label: '帮助中心', path: '/help', icon: 'fas fa-headset' },
];

const USER_INFO_REFRESH_INTERVAL = 15000;
let userInfoRefreshPromise = null;
let lastUserInfoRefreshAt = 0;

const hasLoginToken = () => Boolean(userStore.token || userInfo.value?.token);

const refreshUserInfoSilently = async ({ force = false } = {}) => {
  if (!hasLoginToken()) return null;

  const now = Date.now();
  if (!force && now - lastUserInfoRefreshAt < USER_INFO_REFRESH_INTERVAL) {
    return null;
  }

  if (userInfoRefreshPromise) return userInfoRefreshPromise;

  lastUserInfoRefreshAt = now;
  userInfoRefreshPromise = refreshStoredUserInfo()
    .catch((error) => {
      if (!error?.isAuthError) {
        console.warn('Failed to refresh user info', error);
      }
      return null;
    })
    .finally(() => {
      userInfoRefreshPromise = null;
    });

  return userInfoRefreshPromise;
};

const handleWindowFocus = () => {
  refreshUserInfoSilently();
};

const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    refreshUserInfoSilently();
  }
};

onMounted(() => {
  window.addEventListener('focus', handleWindowFocus);
  document.addEventListener('visibilitychange', handleVisibilityChange);
  refreshUserInfoSilently({ force: true });
});

onUnmounted(() => {
  window.removeEventListener('focus', handleWindowFocus);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  if (cartPulseTimer) window.clearTimeout(cartPulseTimer);
});

// 监听路由变化，如果当前不是首页，则自动收起横幅
watch(() => route?.path, (newPath) => {
  if (newPath && newPath !== '/') {
    isCollapsed.value = true;
  }
}, { immediate: true });

const openAuthDrawer = (type = 'login', redirect = '') => {
  isCollapsed.value = true;
  nextTick(() => {
    authDrawerRef.value?.open(type, redirect);
  });
};

watch(() => route.fullPath, () => {
  if (route.path === '/login' && route.query.loginMode !== 'page') {
    const redirect = Array.isArray(route.query.redirect) ? route.query.redirect[0] : route.query.redirect;
    openAuthDrawer(route.query.type === 'register' ? 'register' : 'login', redirect || '');
    router.replace('/');
  }
}, { immediate: true });

watch(() => route.fullPath, () => {
  refreshUserInfoSilently();
});

// 监听登录状态，自动查询购物车
watch(() => userInfo.value, (newVal) => {
  if (newVal) {
    cartStore.getCartList();
    authDrawerRef.value?.close();
  } else {
    cartStore.resetCartState();
  }
}, { immediate: true });

watch(cartCount, (nextCount, previousCount) => {
  if (Number(nextCount || 0) <= Number(previousCount || 0)) return;
  cartPulse.value = false;
  requestAnimationFrame(() => {
    cartPulse.value = true;
    if (cartPulseTimer) window.clearTimeout(cartPulseTimer);
    cartPulseTimer = window.setTimeout(() => {
      cartPulse.value = false;
      cartPulseTimer = null;
    }, 650);
  });
});

const toggleBanner = () => {
  if (isCollapsed.value) {
    isCollapsed.value = false;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  } else {
    isCollapsed.value = true;
  }
};

const goHome = () => {
  mobileMenuVisible.value = false;
  isCollapsed.value = false;
  router.push('/');
};

const navigateTo = (path) => {
  mobileMenuVisible.value = false;
  isCollapsed.value = path !== '/';
  router.push(path);
};

const handleMobileSearch = () => {
  mobileMenuVisible.value = false;
  isCollapsed.value = false;
  if (route.path !== '/') {
    router.push('/');
  }
  nextTick(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });
};

function goToRoute(val) {
  isCollapsed.value = true; // 点击跳转时立即收缩
  switch (val) {
     case '首页':
      isCollapsed.value = false; // 回到首页展开
      router.push('/');
      break;
    case '品牌专区':
      router.push('/brands');
      break;
    case '走进普洛汇':
      router.push('/puluohui');
      break;
    case '工具':
      router.push('/tools');
      break;
    default:
      break;
  }
}

const handleLogin = () => {
  mobileMenuVisible.value = false;
  openAuthDrawer('login');
};
const handleRegister = () => {
  mobileMenuVisible.value = false;
  openAuthDrawer('register');
};
const handleLogout = () => {
  mobileMenuVisible.value = false;
  ElMessageBox.confirm(
    `<div style="text-align:center;padding:10px 0 0;">
      <div style="width:56px;height:56px;margin:0 auto 16px;border-radius:50%;background:linear-gradient(135deg,#fef3c7,#fde68a);display:flex;align-items:center;justify-content:center;">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#d97706" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
        </svg>
      </div>
      <p style="font-size:16px;font-weight:700;color:#111827;margin:0 0 6px;">确定要退出当前账号吗？</p>
      <p style="font-size:13px;color:#9ca3af;margin:0;">退出后需要重新登录才能继续操作</p>
    </div>`,
    '',
    {
      confirmButtonText: '确定退出',
      cancelButtonText: '取消',
      dangerouslyUseHTMLString: true,
      showClose: true,
      center: true,
      customClass: 'premium-logout-dialog',
      confirmButtonClass: 'premium-logout-confirm-btn',
      cancelButtonClass: 'premium-logout-cancel-btn',
    }
  ).then(async () => {
    await userStore.logout();
    cartStore.resetCartState();
    ElMessage.success('已安全退出');
    router.push('/');
  }).catch(() => undefined);
};

const handleSearch = (event) => {
  const keyword = (searchQuery.value || '').trim();
  searchQuery.value = keyword;
  if (event?.target?.value !== undefined) {
    event.target.value = keyword;
  }
  if (keyword.length < 2) {
    ElMessage.warning("最少输入两个字符");
    return;
  }
  isCollapsed.value = true; // 搜索跳转时立即收缩
  router.push({ path: `/product/DetailSearch/${keyword}` });
};

const goCart = () => {
  mobileMenuVisible.value = false;
  isCollapsed.value = true; // 进购物车时收缩
  router.push('/cart/cart');
};

const goFloatingCart = () => {
  if (!userInfo.value) {
    openAuthDrawer('login', '/cart/cart');
    return;
  }
  goCart();
};

const goPersonal = () => {
  mobileMenuVisible.value = false;
  isCollapsed.value = true; // 进个人中心时收缩
  router.push('/personal/dashboard');
};

const goOrder = () => {
  mobileMenuVisible.value = false;
  isCollapsed.value = true; // 进订单查询时收缩
  router.push('/order/all');
};

const handleOpenAI = () => {
  mobileMenuVisible.value = false;
  if (aiOrderDrawerRef.value) {
    aiOrderDrawerRef.value.openDrawer();
  }
};

const handleAssistantLoginRequired = () => {
  aiOrderDrawerRef.value?.closeDrawer();
  openAuthDrawer('login', route.fullPath);
};

</script>

<style scoped>
.header-wrapper {
  position: relative;
  z-index: 2001;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

.fixed-header-container {
  position: relative;
  width: 100%;
  z-index: 2000;
}

/* --- 1. 顶部灰栏 --- */
.top-bar {
  height: 36px;
  background-color: #032541;
  border-bottom: 1px solid rgba(0, 180, 216, 0.15);
  font-size: 12px;
  line-height: 36px;
}

.system-info {
  color: #a1a1aa;
  max-width: 400px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.top-actions {
  white-space: nowrap;
  flex-shrink: 0;
}

.top-auth-group {
  display: inline-flex;
  align-items: center;
  gap: 18px;
}

.auth-link {
  color: #a1a1aa;
  cursor: pointer;
  transition: 0.3s;
}

.auth-link:hover {
  color: #ffffff;
}

.login-action,
.register-action {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.register-action {
  margin-left: 2px;
}

.user-welcome-link {
  color: #e0f7ff;
  font-weight: 700;
  cursor: pointer;
  max-width: 190px;
  height: 24px;
  padding: 0 9px 0 3px;
  border-radius: 14px;
  background: rgba(0, 180, 216, 0.14);
  border: 1px solid rgba(0, 180, 216, 0.32);
  white-space: nowrap;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  vertical-align: middle;
}

.user-avatar-mini {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: linear-gradient(135deg, #0077B6, #00B4D8);
  color: #fff;
  font-size: 11px;
  font-weight: 800;
}

.user-welcome-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.logout-btn {
  color: #71717a;
  font-size: 12px;
}

.logout-btn:hover {
  color: #ef4444;
}

.top-cart-badge :deep(.el-badge__content) {
  background-color: #00B4D8 !important;
  border: none;
  height: 16px;
  line-height: 16px;
  padding: 0 4px;
}

/* --- 2. 主导航及 Hero 区 --- */
.main-header {
  background-color: #032541;
  background-image: radial-gradient(circle at 20% 50%, rgba(0, 180, 216, 0.12) 0%, transparent 40%),
                    radial-gradient(circle at 80% 80%, rgba(0, 87, 160, 0.1) 0%, transparent 30%);
  transition: all 0.6s cubic-bezier(0.25, 1, 0.5, 1);
  overflow: hidden;
  border-bottom-left-radius: 48px;
  border-bottom-right-radius: 48px;
}

.main-header.is-collapsed-header {
  background-color: rgba(3, 37, 65, 0.98);
  backdrop-filter: blur(20px);
  box-shadow: 0 4px 30px rgba(0, 59, 111, 0.3);
  border-bottom-left-radius: 24px;
  border-bottom-right-radius: 24px;
}

.nav-links-center {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 40px;
}

.nav-item-text {
  color: #ffffff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.3s;
}

.nav-item-text:hover {
  color: #00B4D8;
}

@keyframes gradientShift {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

/* 迷你搜索框过渡动画 */
.mini-search-bar {
  width: 0;
  opacity: 0;
  overflow: hidden;
  transition: all 0.5s cubic-bezier(0.25, 1, 0.5, 1);
}
.mini-search-bar.is-active {
  width: 300px;
  opacity: 1;
  margin-right: 16px;
}

.dark-mini-input :deep(.el-input__wrapper) {
  background-color: rgba(255, 255, 255, 0.08) !important;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.15) inset !important;
  border-radius: 8px;
  padding: 0 6px 0 16px;
  transition: all 0.3s ease;
}
.dark-mini-input :deep(.el-input__wrapper.is-focus) {
  background-color: rgba(255, 255, 255, 0.12) !important;
  box-shadow: 0 0 0 1px rgba(0, 180, 216, 0.5) inset !important; 
}
.dark-mini-input :deep(.el-input__inner) {
  color: #ffffff;
  height: 38px;
  font-size: 13px;
}
.dark-mini-input :deep(.el-input__inner::placeholder) {
  color: #94a3b8;
}

.ai-pill-btn {
  border: 1px solid rgba(255, 255, 255, 0.18);
  background-color: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  padding: 9px 18px;
  border-radius: 40px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
}
.ai-pill-btn:hover {
  transform: translateY(-2px);
  border-color: rgba(125, 211, 252, 0.55);
  background: rgba(0, 180, 216, 0.18);
  box-shadow: 0 8px 25px rgba(0, 180, 216, 0.16);
}
.ai-icon-gradient {
  color: #67e8f9;
  font-size: 18px;
}

.floating-action-stack {
  position: fixed;
  right: 40px;
  bottom: 251px;
  z-index: 2002;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.floating-cart-entry,
.ai-floating-entry {
  position: relative;
  display: flex;
  width: 56px;
  min-height: 66px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border: 0;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.94);
  color: #0f172a;
  box-shadow: 0 8px 32px rgba(0, 50, 150, 0.12);
  cursor: pointer;
  font-size: 10px;
  font-weight: 800;
  transition: all 0.25s ease;
}

.floating-cart-entry {
  border: 1px solid rgba(0, 119, 182, 0.12);
  color: #0077b6;
}

.floating-cart-entry:hover,
.floating-cart-entry:focus-visible,
.ai-floating-entry:hover {
  transform: translateY(-3px);
  background: #0077b6;
  color: #fff;
}

.floating-cart-entry:focus-visible,
.ai-floating-entry:focus-visible {
  outline: 3px solid rgba(14, 165, 233, 0.28);
  outline-offset: 2px;
}

.floating-cart-entry.is-pulsing {
  animation: floating-cart-pop 650ms cubic-bezier(0.22, 1, 0.36, 1);
}

.floating-cart-icon {
  font-size: 22px;
}

.floating-cart-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  display: grid;
  min-width: 20px;
  height: 20px;
  place-items: center;
  padding: 0 5px;
  border: 2px solid #fff;
  border-radius: 999px;
  background: #ef4444;
  color: #fff;
  font-size: 10px;
  font-weight: 800;
  line-height: 1;
  box-sizing: border-box;
}

@keyframes floating-cart-pop {
  0%, 100% { transform: translateY(0) scale(1); }
  45% { transform: translateY(-5px) scale(1.08); }
}

.ai-floating-logo {
  display: block;
  width: 30px;
  height: 30px;
  object-fit: contain;
}

.mobile-menu-ai {
  color: #0369a1;
  background: #eff8ff;
}

/* --- 3. Hero 内容样式 --- */
.hero-transition-wrapper {
  display: grid;
  grid-template-rows: 1fr;
  transition: grid-template-rows 0.6s cubic-bezier(0.25, 1, 0.5, 1), 
              opacity 0.5s ease;
  opacity: 1;
}

.hero-transition-wrapper.is-collapsed {
  grid-template-rows: 0fr;
  opacity: 0;
  pointer-events: none; 
}

.hero-content-inner {
  min-height: 0; 
}

.badge-tag {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #e2e8f0;
  font-size: 14px;
  padding: 8px 20px;
  border-radius: 100px;
  display: inline-flex;
  align-items: center;
  backdrop-filter: blur(10px);
}

.hero-title {
  color: #ffffff;
  font-size: 56px;
  font-weight: 800;
  letter-spacing: -1px;
  line-height: 1.2;
}

.gradient-text {
  background: linear-gradient(to right, #00B4D8, #0077B6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.hero-subtitle {
  color: #94a3b8;
  font-size: 18px;
  line-height: 1.6;
}

.hero-search-wrapper {
  width: 100%;
  max-width: 720px;
}

.hero-search-box {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 8px;
  display: flex;
  align-items: center;
  backdrop-filter: blur(10px);
  transition: all 0.3s;
}
.hero-search-box:focus-within {
  border-color: rgba(0, 180, 216, 0.5);
  background: rgba(255, 255, 255, 0.08);
}
.search-prefix-icon {
  color: #71717a;
  font-size: 20px;
  margin: 0 15px;
}
.hero-search-box input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: #ffffff;
  font-size: 16px;
  padding: 10px 0;
}
.hero-search-box input::placeholder {
  color: #94a3b8;
}

.hero-search-btn {
  background: linear-gradient(135deg, #0077B6, #00B4D8);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 10px 24px;
  font-weight: 700;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  margin-left: 10px;
}
.hero-search-btn:hover {
  background: linear-gradient(135deg, #005F8A, #0098B5);
  transform: translateX(3px);
}

.header-logo-img {
  height: 72px;
  width: auto;
  object-fit: contain;
  border-radius: 10px;
  padding: 4px 10px;
}

.mobile-header-actions {
  display: none;
}

.mobile-icon-btn,
.mobile-auth-btn {
  border: 0;
  white-space: nowrap;
  touch-action: manipulation;
}

.mobile-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.1);
  color: #e0f7ff;
  font-size: 14px;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.12);
}

.mobile-auth-btn {
  height: 34px;
  padding: 0 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  color: #e0f7ff;
  font-size: 12px;
  font-weight: 800;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.14);
}

.mobile-auth-btn.is-primary {
  background: #00b4d8;
  color: #ffffff;
  box-shadow: 0 8px 18px rgba(0, 180, 216, 0.2);
}

.mobile-cart-badge :deep(.el-badge__content) {
  min-width: 14px;
  height: 14px;
  padding: 0 4px;
  border: 0;
  background: #ef4444;
  font-size: 10px;
  line-height: 14px;
}

:global(.mobile-menu-drawer .el-drawer__body) {
  padding: 0 !important;
  overflow: hidden;
  background: #f8fafc;
}

.mobile-menu-panel {
  display: flex;
  flex-direction: column;
  height: 100dvh;
  min-height: 0;
  padding: max(18px, env(safe-area-inset-top, 0px)) 16px max(18px, env(safe-area-inset-bottom, 0px));
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.mobile-menu-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
}

.mobile-menu-head img {
  width: 98px;
  height: auto;
}

.mobile-menu-head button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 12px;
  background: #ffffff;
  color: #64748b;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.06);
}

.mobile-menu-search {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  margin-bottom: 16px;
  padding: 0 14px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #ffffff;
  color: #64748b;
  font-size: 13px;
}

.mobile-menu-search i {
  color: #00b4d8;
}

.mobile-menu-section {
  display: grid;
  gap: 10px;
  padding: 12px 0;
  border-top: 1px solid #e5e7eb;
}

.mobile-menu-item,
.mobile-menu-cta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 44px;
  border: 0;
  border-radius: 14px;
  background: #ffffff;
  color: #0f172a;
  font-size: 14px;
  font-weight: 800;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.05);
}

.mobile-menu-item {
  padding: 0 14px;
}

.mobile-menu-item span {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  gap: 10px;
}

.mobile-menu-item span i {
  width: 18px;
  color: #00b4d8;
  text-align: center;
}

.mobile-menu-item > i {
  color: #cbd5e1;
  font-size: 11px;
}

.mobile-menu-cta {
  justify-content: center;
  padding: 0 14px;
  background: linear-gradient(135deg, #0077b6, #00b4d8);
  color: #ffffff;
}

.mobile-menu-cta.is-outline {
  background: #ffffff;
  color: #0077b6;
  box-shadow: inset 0 0 0 1px #bae6fd, 0 8px 18px rgba(15, 23, 42, 0.04);
}

.mobile-menu-cta.is-danger {
  color: #ef4444;
  box-shadow: inset 0 0 0 1px #fecaca, 0 8px 18px rgba(15, 23, 42, 0.04);
}

@media (max-width: 1024px) {
  .hero-title { font-size: 40px; }
  .nav-links-center { gap: 20px; }
  .mini-search-bar.is-active { width: 180px; }
  .header-logo-img { height: 56px; }
  .floating-action-stack { right: 16px; }
}

@media (max-width: 768px) {
  .top-bar { display: none !important; }
  .main-header {
    border-bottom-left-radius: 16px !important;
    border-bottom-right-radius: 16px !important;
  }
  .main-header.is-collapsed-header {
    border-bottom-left-radius: 12px !important;
    border-bottom-right-radius: 12px !important;
  }
  .nav-row {
    height: 56px !important;
    gap: 8px;
  }
  .header-logo-img {
    height: 42px !important;
    padding: 2px 4px !important;
  }
  .logo-box {
    min-width: 0;
    flex: 0 1 auto;
  }
  .nav-links-center { display: none !important; }
  .ai-pill-btn { display: none !important; }
  .header-right-tools,
  .mini-search-bar { display: none !important; }
  .mobile-header-actions {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    flex: 0 0 auto;
    min-width: 0;
  }
  .hero-title { font-size: 26px !important; line-height: 1.3 !important; }
  .gradient-text { font-size: inherit !important; }
  .hero-subtitle { font-size: 14px !important; }
  .badge-tag { font-size: 12px !important; padding: 6px 14px !important; }
  .hero-search-box {
    border-radius: 12px !important;
    padding: 6px !important;
  }
  .hero-search-btn {
    padding: 8px 16px !important;
    font-size: 13px !important;
    border-radius: 8px !important;
  }
  .search-prefix-icon { margin: 0 10px !important; font-size: 16px !important; }
  .hero-search-box input { font-size: 14px !important; }
  .hero-content-inner > div {
    padding-top: 24px !important;
    padding-bottom: 28px !important;
  }
  .floating-action-stack {
    right: 14px;
    bottom: 84px;
  }
  .floating-cart-entry { display: none; }
}

@media (max-width: 480px) {
  .hero-title { font-size: 22px; }
  .hero-subtitle { font-size: 13px; }
  .hero-search-btn { padding: 6px 12px; font-size: 12px; }
  .header-logo-img { height: 36px; }
  .mobile-header-actions {
    gap: 5px;
  }
  .mobile-icon-btn {
    width: 32px;
    height: 32px;
    border-radius: 11px;
  }
  .mobile-ai-btn {
    background: rgba(0, 180, 216, 0.2);
    color: #67e8f9;
  }
  .ai-floating-entry {
    width: 52px;
    min-height: 60px;
  }
  .mobile-auth-btn {
    height: 32px;
    padding: 0 8px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .floating-cart-entry,
  .ai-floating-entry {
    transition: none;
  }
  .floating-cart-entry.is-pulsing {
    animation: none;
  }
}
</style>

<style>
/* 退出确认弹窗全局样式 (不能 scoped，因为 ElMessageBox 挂在 body) */
.premium-logout-dialog {
  border-radius: 16px !important;
  overflow: hidden;
  padding: 28px 24px 20px !important;
  max-width: 380px !important;
}
.premium-logout-dialog .el-message-box__header {
  display: none !important;
}
.premium-logout-dialog .el-message-box__status {
  display: none !important;
}
.premium-logout-dialog .el-message-box__content {
  padding: 0 !important;
}
.premium-logout-dialog .el-message-box__btns {
  padding: 18px 0 0 !important;
  display: flex;
  justify-content: center;
  gap: 12px;
}
.premium-logout-confirm-btn {
  background: linear-gradient(135deg, #ef4444, #dc2626) !important;
  border: none !important;
  border-radius: 10px !important;
  padding: 10px 32px !important;
  font-weight: 700 !important;
  font-size: 14px !important;
  color: #fff !important;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3) !important;
}
.premium-logout-confirm-btn:hover {
  background: linear-gradient(135deg, #dc2626, #b91c1c) !important;
}
.premium-logout-cancel-btn {
  border-radius: 10px !important;
  padding: 10px 32px !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  border-color: #e5e7eb !important;
  color: #6b7280 !important;
}
.premium-logout-cancel-btn:hover {
  border-color: #d1d5db !important;
  background: #f9fafb !important;
}
</style>
